import React from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
  CCASSQueryDetailsResponse,
  CCASSShareholdingDetail,
  CCASSSummary,
} from '../../../py-generated/datalouder/ccass/types';
import { getParticipantName } from './CCASSShareholdingTable';
import { NOT_IN_CCASS } from './CCASSSummaryTables';

interface Props {
  querySummary: CCASSSummary;
  queryDetails: CCASSShareholdingDetail[];
  compareSummary: CCASSSummary;
  compareDetails: CCASSShareholdingDetail[];
  participants: CCASSQueryDetailsResponse['participants'];
  desc?: boolean;
}

const ShareholdingChangeChart: React.FC<Props> = ({
  querySummary,
  queryDetails,
  compareSummary,
  compareDetails,
  participants,
  desc,
}) => {
  const { t, i18n } = useTranslation();

  const _getParticipantName = (item: CCASSShareholdingDetail) =>
    getParticipantName(
      participants,
      item.participant_id,
      item.participant_name
    )[i18n.language];

  const allParticipants: Set<string> = new Set();
  allParticipants.add(t(NOT_IN_CCASS));

  const compareShareholdings: Record<string, number> = {
    [t(NOT_IN_CCASS)]:
      compareSummary.issued_shares - compareSummary.total_ccass,
  };
  for (const item of compareDetails) {
    const name = _getParticipantName(item);
    compareShareholdings[name] = item.share_holding;
    allParticipants.add(name);
  }

  const queryShareholdings: Record<string, number> = {
    [t(NOT_IN_CCASS)]: querySummary.issued_shares - querySummary.total_ccass,
  };
  for (const item of queryDetails) {
    const name = _getParticipantName(item);
    queryShareholdings[name] = item.share_holding;
    allParticipants.add(name);
  }

  const shareholdingChanges = [...allParticipants]
    .map(name => {
      const after = queryShareholdings[name] || 0;
      const before = compareShareholdings[name] || 0;
      return {
        name,
        before,
        after,
        change: after - before,
      };
    })
    .sort((a, b) => (desc ? b.change - a.change : a.change - b.change))
    .filter(({ change }) => (desc ? change > 0 : change < 0))
    .slice(0, 10);

  const data = {
    labels: shareholdingChanges.map(({ name }) => name),
    datasets: [
      {
        data: shareholdingChanges.map(({ change }) => change),
        backgroundColor: desc ? 'rgb(34, 187, 3)' : 'rgb(187, 9, 3)',
      },
    ],
  };

  return (
    <HorizontalBar
      data={data}
      options={{
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: t(desc ? 'buy-in-ranking' : 'sell-out-ranking'),
        },
        responsive: true,
        maintainAspectRatio: true,
      }}
    />
  );
};

export default ShareholdingChangeChart;
